import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Card, Typography, Avatar, IconButton,
    InputAdornment, FormControlLabel, Checkbox
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { loginUser } from '../services/authService';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const Login = () => {
    const [credentials, setCredentials] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    const handleChange = (e) => {
        setCredentials({ ...credentials, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const { access_token, user } = await loginUser(credentials);
            login(access_token, user);
            navigate('/devices');
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    // Auto-clear error message
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    return (
        <Box
            sx={{
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'linear-gradient(180deg, #01183b 0%, #2c3e55 100%)',
            }}
        >
            <Card
                sx={{
                    p: 2,
                    maxWidth: 320,
                    bgcolor: 'grey.200',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderRadius: 3,
                    boxShadow: 6,
                }}
            >
                <Avatar alt="Logo" src="/sr4dx_favicon.png" sx={{ width: 68, height: 68, mb: 2, p: 1 }} />

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1e2a38', textAlign: 'center', mb: 2 }}>
                    MoChaPo Admin Hub
                </Typography>

                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Email"
                        name="email"
                        value={credentials.email}
                        onChange={handleChange}
                        required
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={credentials.password}
                        onChange={handleChange}
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton sx={{ mr: 1 }} onClick={togglePasswordVisibility} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                name="rememberMe"
                            />
                        }
                        label="Remember Me"
                        componentsProps={{
                            typography: {
                                sx: { fontSize: '14px' },
                            },
                        }}
                        sx={{ ml: "auto" }}
                    />

                    {error && (
                        <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 1 }}>
                            {error}
                        </Typography>
                    )}
                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{ mt: 2, backgroundColor: '#01183b', color: 'white' }}
                    >
                        {loading ? 'Logging in...' : 'Login'}
                    </Button>
                </form>
            </Card>
        </Box>
    );
};

export default Login;
